import { MAIN_IMAGE_ROOT } from 'app_config';
import type { LoggedUserCompany, ReportSignature } from 'common/models';
import { useGetCompanyAndUserInfoQuery } from 'common/queries';
import useAuth from 'common/useAuth';
import { ImageComponent, Modal, SignatureComponent } from 'components';
import { getFullName } from 'helpers/contactPersonHelpers';
import { useGeolocation } from 'helpers/geolocationHelper';
import { renderDate } from 'helpers/timeHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { ContactPerson } from 'pages/customers/models';
import { useSignInterventionReportMutation } from 'pages/interventions/queries';
import React, { useState } from 'react';

interface Props {
  withDate?: boolean;
  withName?: boolean;
  withPosition?: boolean;
  customDate?: string;
  customName?: string;
  customPosition?: string;
  reportId: string;
  customFormId: string;
  orderId: string;
  serviceObjectId?: string;
  signature?: ReportSignature;
  canSign?: boolean;
  perServiceObject?: boolean;
  contactPerson: ContactPerson;
  company?: LoggedUserCompany;
  interventionId: string;
  primaryAssigneeId: string;
  isPublic?: boolean;
  customTitle?: string;
}

const CustomerSignatureBlock: React.FC<Props> = (props): React.ReactElement => {
  const {
    withDate,
    withName,
    withPosition,
    customDate,
    customName,
    customPosition,
    reportId,
    customFormId,
    orderId,
    serviceObjectId,
    signature,
    canSign,
    perServiceObject,
    contactPerson,
    company,
    interventionId,
    primaryAssigneeId,
    isPublic,
    customTitle
  } = props;

  const { t, i18n } = useCustomTranslation();
  const { requestLocation } = useGeolocation({ disableGeolocation: isPublic, timeout: 10000 });
  const { authenticated } = useAuth();

  const companyAndUserQuery = useGetCompanyAndUserInfoQuery(authenticated);
  const signInterventionReportMutation = useSignInterventionReportMutation();

  const user = companyAndUserQuery.data?.user;

  const [showSignatureComponent, setShowSignatureComponent] = useState(false);
  const isPrimaryAssignee = user?.id === primaryAssigneeId;

  const handleSaveSignature = async (formData: FormData, reportStage: (stage: string) => void) => {
    const locationRequired = (company?.requireTechnicianLocation && isPrimaryAssignee) || false;
    reportStage(locationRequired ? 'location-required' : 'location-optional');
    const geolocationResult = await requestLocation(locationRequired);

    if (locationRequired && geolocationResult?.error) {
      throw new Error('Location is required to save signature');
    }

    if (geolocationResult?.coords) {
      formData.append('latitude', geolocationResult?.coords.latitude.toString());
      formData.append('longitude', geolocationResult?.coords.longitude.toString());
    }

    serviceObjectId && perServiceObject && formData.append('serviceObjectId', serviceObjectId);

    reportStage('mutation-and-refresh');
    await signInterventionReportMutation.mutateAsync(
      {
        orderId,
        customFormId,
        reportId,
        interventionId,
        data: formData
      },
      { onSuccess: () => setShowSignatureComponent(false) }
    );
  };

  const renderCustomerSignatureContent = () =>
    canSign ? (
      <div className="sign" onClick={() => setShowSignatureComponent(true)}>
        {t('customData.signature.signIntervention')}
      </div>
    ) : (
      <div className="placeholder">{t('customData.signature.signaturePlaceholder')}</div>
    );

  return (
    <div className="CustomerSignature">
      <div className={`content ${canSign ? 'enabled' : 'disabled'}`}>
        <div className="heading">{customTitle || t('customData.signature.customerSignature')}</div>
        {signature ? (
          <div onClick={() => canSign && setShowSignatureComponent(true)}>
            <div
              className="images_wrapper"
              // style={{ height: imageHeight || undefined }}
            >
              <ImageComponent url={`${MAIN_IMAGE_ROOT}${signature?.publicUrl}`} />
            </div>
            {withDate && (
              <div className="date">
                {customDate
                  ? renderDate(customDate, 'LL', i18n.language)
                  : renderDate(signature.created, 'LL', i18n.language)}
              </div>
            )}
            {withName && <div className="name">{customName || getFullName(contactPerson)}</div>}
            {withPosition && (
              <div className="position">{customPosition || contactPerson.position}</div>
            )}
          </div>
        ) : (
          renderCustomerSignatureContent()
        )}
      </div>
      <Modal
        open={showSignatureComponent}
        onClose={() => setShowSignatureComponent(false)}
        title={t('customData.signature.customerSignatureTitle')}
      >
        <SignatureComponent
          saveSignature={handleSaveSignature}
          fileName="signature"
          diagnosticSource="customer-report"
        />
      </Modal>
    </div>
  );
};

export default CustomerSignatureBlock;
